// SPDX-License-Identifier: AGPL-3.0-or-later
import * as VoiceCallLayoutCommands from '@app/features/voice/commands/VoiceCallLayoutCommands';
import {getStreamKey, parseStreamKey} from '@app/features/voice/components/StreamKeys';
import {parseVoiceParticipantIdentity} from '@app/features/voice/components/VoiceParticipantSpeakingUtils';
import {syncLocalVoiceStateWithServer} from '@app/features/voice/engine/VoiceMediaEngineBridge';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import VoiceCallLayout from '@app/features/voice/state/VoiceCallLayout';
import {ME} from '@app/vendor/constants/AppConstants';
import {Track} from 'livekit-client';

interface StopWatchingStreamKeyOptions {
	guildId?: string | null;
	channelId?: string | null;
	clearPinned?: boolean;
	sync?: boolean;
}
interface PruneInactiveWatchedStreamsOptions {
	guildId?: string | null;
	channelId: string;
	isStreamActive: (connectionId: string) => boolean;
}
export function normalizeStreamGuildId(guildId: string | null | undefined): string | null {
	if (!guildId || guildId === ME) return null;
	return guildId;
}
export function getStreamKeyForParticipantIdentity(
	guildId: string | null | undefined,
	channelId: string | null | undefined,
	participantIdentity: string,
): string | null {
	const {connectionId} = parseVoiceParticipantIdentity(participantIdentity);
	if (!connectionId || !channelId) return null;
	return getStreamKey(normalizeStreamGuildId(guildId), channelId, connectionId);
}
function getPinnedScreenShareStreamKey(
	guildId: string | null | undefined,
	channelId: string | null | undefined,
): string | null {
	if (!channelId) return null;
	if (VoiceCallLayout.pinnedParticipantSource !== Track.Source.ScreenShare) return null;
	const pinnedIdentity = VoiceCallLayout.pinnedParticipantIdentity;
	if (!pinnedIdentity) return null;
	return getStreamKeyForParticipantIdentity(guildId, channelId, pinnedIdentity);
}
function clearPinnedScreenShareIfMatches(
	streamKey: string,
	guildId: string | null | undefined,
	channelId: string | null | undefined,
): void {
	if (getPinnedScreenShareStreamKey(guildId, channelId) !== streamKey) return;
	VoiceCallLayoutCommands.setPinnedParticipant(null);
}
export function stopWatchingStreamKey(streamKey: string, options: StopWatchingStreamKeyOptions = {}): boolean {
	const {guildId = null, channelId = null, clearPinned = true, sync = true} = options;
	const current = LocalVoiceState.getViewerStreamKeys();
	if (!current.includes(streamKey)) return false;
	const updated = current.filter((key) => key !== streamKey);
	LocalVoiceState.updateViewerStreamKeys(updated);
	if (sync) {
		syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
	}
	if (clearPinned) {
		clearPinnedScreenShareIfMatches(streamKey, guildId, channelId);
	}
	return true;
}
export function pruneInactiveWatchedStreamsForChannel({
	guildId,
	channelId,
	isStreamActive,
}: PruneInactiveWatchedStreamsOptions): Array<string> {
	const normalizedGuildId = normalizeStreamGuildId(guildId);
	const current = LocalVoiceState.getViewerStreamKeys();
	if (current.length === 0) return [];
	const removed: Array<string> = [];
	const updated = current.filter((streamKey) => {
		const parsed = parseStreamKey(streamKey);
		if (!parsed) return true;
		if (parsed.channelId !== channelId || parsed.guildId !== normalizedGuildId) return true;
		if (isStreamActive(parsed.connectionId)) return true;
		removed.push(streamKey);
		return false;
	});
	if (removed.length === 0) return [];
	LocalVoiceState.updateViewerStreamKeys(updated);
	syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
	for (const streamKey of removed) {
		clearPinnedScreenShareIfMatches(streamKey, normalizedGuildId, channelId);
	}
	return removed;
}
